import { ComponentGalleryBasic } from '../../../../common/types/api/page'
import { MAX_WIDTH } from '../emailType/getGalleryLayout'

const ERROR_MSG = {
    INVALID_INPUT: 'Invalid input',
    CANNOT_MOVE_UP: 'You cannot move up this element',
    CANNOT_MOVE_DOWN: 'You cannot move down this element',
    CANNOT_MOVE_LEFT: 'You cannot move this element for the left position',
    CANNOT_MOVE_RIGHT: 'You cannot move this element for the left position',
    CANNOT_MOVE_UP_NEW_LINE: 'This row has not enough elements to move up to a new row',
    CANNOT_MOVE_DOWN_NEW_LINE: 'This row has not enough elements to move down to a new row',
}

enum MovementDirection {
    UP, RIGHT, DOWN, LEFT,
    UP_NEW_LINE, DOWN_NEW_LINE
}

const isValidInput = function (table: ComponentGalleryBasic[][], row: number, col: number): boolean {
    return (
        table !== undefined &&
        row !== undefined &&
        col !== undefined &&
        row >= 0 &&
        col >= 0 &&
        row < table.length &&
        col < table[row].length &&
        table[row][col] !== undefined
    )
}

const copyTable = function (table: ComponentGalleryBasic[][]): ComponentGalleryBasic[][] {
    const result = [] as ComponentGalleryBasic[][]
    for (let r = 0; r < table.length; r++) {
        result.push([...table[r]])
    }
    return result
}

const findLineWithEnoughSpace = function(table: ComponentGalleryBasic[][], contentSize: number) {
    return table
        .reduce((currentState, collumn, index) => {
            const currentWidthfreeSpace =
                MAX_WIDTH - collumn.reduce((s, { width }) => s + width, 0)

            if (currentWidthfreeSpace >= contentSize) {
                currentState.push(index)
            }
            return currentState
        }, [] as number[])
        .sort()
}

// this function is for code reuse, don't import it, it has no validations
const moveVertically = function(direction: MovementDirection, table: ComponentGalleryBasic[][], row: number, col: number): ComponentGalleryBasic[][] {
    if(direction !== MovementDirection.DOWN && direction != MovementDirection.UP)
        return table
    
    const filterCallback = 
        direction === MovementDirection.UP ? (r, idx) => idx < row :
        (r, idx) => idx > row 
    
    const availableRows = findLineWithEnoughSpace(
        table.filter(filterCallback), 
        table[row][col].width
    )

    const result = copyTable(table)
    let rowToMove
    let resultOriginalRow = row

    if(availableRows.length > 0) {
        if(direction === MovementDirection.UP)
            availableRows.reverse()
        
        rowToMove = availableRows[0] 

        if(direction === MovementDirection.DOWN)
            rowToMove += row + 1
    } else { //in case that it needs to create a line
        if(direction === MovementDirection.UP) {
            result.splice(row - 1, 0, [])
            rowToMove = row - 1
            resultOriginalRow = row + 1
        } else {
            //in the documentation says that if the start is greater than the length...
            //it should work like an add function
            result.splice(row + 2, 0, [])
            rowToMove = row + 2
        }
    }

    result[rowToMove].push(table[row][col])
    result[resultOriginalRow].splice(col, 1)
    if(result[resultOriginalRow].length == 0){
        result.splice(resultOriginalRow,1)
    }

    return result
}

const moveUp = function (table: ComponentGalleryBasic[][], row: number, col: number): ComponentGalleryBasic[][] {
    if (!canMoveUp(table, row, col)) throw new Error(ERROR_MSG.CANNOT_MOVE_UP)

    return moveVertically(MovementDirection.UP, table, row, col)
}
const moveDown = function (table: ComponentGalleryBasic[][], row: number, col: number): ComponentGalleryBasic[][] {
    if (!canMoveDown(table, row, col)) throw new Error(ERROR_MSG.CANNOT_MOVE_DOWN)

    return moveVertically(MovementDirection.DOWN, table, row, col)
}
const moveLeft = function (table: ComponentGalleryBasic[][], row: number, col: number): ComponentGalleryBasic[][] {
    if (!canMoveLeft(table, row, col)) throw new Error(ERROR_MSG.CANNOT_MOVE_LEFT)

    const result = copyTable(table)
    result[row][col - 1] = table[row][col]
    result[row][col] = table[row][col - 1]

    return result
}
const moveRight = function (table: ComponentGalleryBasic[][], row: number, col: number): ComponentGalleryBasic[][] {
    if (!canMoveRight(table, row, col)) throw new Error(ERROR_MSG.CANNOT_MOVE_RIGHT)

    const result = copyTable(table)
    result[row][col + 1] = table[row][col]
    result[row][col] = table[row][col + 1]

    return result
}

const moveNewLineUp = function (table: ComponentGalleryBasic[][], row: number, col: number): ComponentGalleryBasic[][] {
    if (!canMoveNewLineUp(table, row, col)) throw new Error(ERROR_MSG.CANNOT_MOVE_UP_NEW_LINE)
    const result = copyTable(table)
    result[row].splice(col, 1)
    result.splice(row, 0, [table[row][col]])

    return result.filter(arr => arr.length !== 0)
}
const moveNewLineDown = function (
    table: ComponentGalleryBasic[][],
    row: number,
    col: number
): ComponentGalleryBasic[][] {
    if (!canMoveNewLineDown(table, row, col)) throw new Error(ERROR_MSG.CANNOT_MOVE_DOWN_NEW_LINE)

    const result = copyTable(table)
    result[row].splice(col, 1)
    result.splice(row + 1, 0, [table[row][col]])

    return result.filter(arr => arr.length !== 0)
}

const canMoveUp = function (table: ComponentGalleryBasic[][], row: number, col: number): boolean {
    if (!isValidInput(table, row, col)) throw new Error(ERROR_MSG.INVALID_INPUT)
    return row > 0
}
const canMoveDown = function (table: ComponentGalleryBasic[][], row: number, col: number): boolean {
    if (!isValidInput(table, row, col)) throw new Error(ERROR_MSG.INVALID_INPUT)
    return row < table.length - 1
}
const canMoveLeft = function (table: ComponentGalleryBasic[][], row: number, col: number): boolean {
    if (!isValidInput(table, row, col)) throw new Error(ERROR_MSG.INVALID_INPUT)
    return col > 0
}
const canMoveRight = function (table: ComponentGalleryBasic[][], row: number, col: number): boolean {
    if (!isValidInput(table, row, col)) throw new Error(ERROR_MSG.INVALID_INPUT)
    return col < table[row].length - 1
}

const canMoveNewLineUp = function (table: ComponentGalleryBasic[][], row: number, col: number): boolean {
    if (!isValidInput(table, row, col)) throw new Error(ERROR_MSG.INVALID_INPUT)
    return table[row].length > 1
}
const canMoveNewLineDown = function (table: ComponentGalleryBasic[][], row: number, col: number): boolean {
    if (!isValidInput(table, row, col)) throw new Error(ERROR_MSG.INVALID_INPUT)
    return table[row].length > 1
}

export {
    moveUp,
    moveDown,
    moveLeft,
    moveRight,
    moveNewLineUp,
    moveNewLineDown,
    canMoveUp,
    canMoveDown,
    canMoveLeft,
    canMoveRight,
    canMoveNewLineUp,
    canMoveNewLineDown,
    ERROR_MSG
}
